/**
 * Admin Campaign Statistics Page
 * Allows admins to view detailed campaign logs with advanced filtering
 */

import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import {
  getCampaignLogs,
  getAccounts,
  getCampaigns,
  getCampaignDetail,
  exportCampaignLogs,
} from '../../services/admin/campaignStatistics';
import type {
  CampaignLogsFilters,
  AccountOption,
  CampaignOption,
  CampaignLogEntry,
  CampaignDetail,
} from '../../types/adminStatistics';

export default function AdminCampaignStatisticsPage() {
  const [filters, setFilters] = useState<CampaignLogsFilters>({
    start_date: format(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
    end_date: format(new Date(), 'yyyy-MM-dd'),
    page: 1,
    per_page: 50,
  });

  const [selectedAccount, setSelectedAccount] = useState<number | null>(null);
  const [selectedCampaign, setSelectedCampaign] = useState<number | null>(null);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  // Fetch accounts
  const { data: accounts = [] } = useQuery<AccountOption[]>({
    queryKey: ['admin', 'accounts'],
    queryFn: () => getAccounts(),
  });

  // Fetch campaigns for selected account
  const { data: campaigns = [] } = useQuery<CampaignOption[]>({
    queryKey: ['admin', 'campaigns', selectedAccount],
    queryFn: () => getCampaigns(selectedAccount!),
    enabled: !!selectedAccount,
  });

  // Fetch campaign detail when a specific campaign is selected
  const { data: campaignDetail } = useQuery<CampaignDetail>({
    queryKey: ['admin', 'campaign-detail', selectedCampaign],
    queryFn: () => getCampaignDetail(selectedCampaign!),
    enabled: !!selectedCampaign,
  });

  // Fetch logs
  const { data: logsData, isLoading, refetch } = useQuery({
    queryKey: ['admin', 'campaign-logs', filters],
    queryFn: () => getCampaignLogs(filters),
    keepPreviousData: true,
  });

  // Reset campaign selection when account changes
  useEffect(() => {
    setSelectedCampaign(null);
    setFilters(prev => ({ ...prev, campaign_id: null, page: 1 }));
  }, [selectedAccount]);

  // Update filters when selections change
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      account_id: selectedAccount,
      campaign_id: selectedCampaign,
      page: 1,
    }));
  }, [selectedAccount, selectedCampaign]);

  const handleFilterChange = (key: keyof CampaignLogsFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      // Only reset to page 1 if we're changing a filter other than page
      ...(key !== 'page' && { page: 1 }),
    }));
  };

  const toggleRowExpansion = (requestId: string) => {
    setExpandedRow(expandedRow === requestId ? null : requestId);
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      await exportCampaignLogs({
        account_id: filters.account_id,
        campaign_id: filters.campaign_id,
        start_date: filters.start_date,
        end_date: filters.end_date,
        country_include: filters.country_include,
        country_exclude: filters.country_exclude,
        language: filters.language,
        answer: filters.answer,
      });
      toast.success('Export completed successfully');
    } catch (error) {
      toast.error('Failed to export logs');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="max-w-full mx-auto py-8 px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Campaign Statistics (Admin)</h1>
        <p className="mt-1 text-sm text-gray-500">
          View detailed campaign logs and traffic statistics
        </p>
      </div>

      {/* Filters Section */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Filters</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Account Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              User Account
            </label>
            <select
              value={selectedAccount || ''}
              onChange={(e) => setSelectedAccount(e.target.value ? Number(e.target.value) : null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Accounts</option>
              {accounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.display_name}
                </option>
              ))}
            </select>
          </div>

          {/* Campaign Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Campaign Name
            </label>
            <select
              value={selectedCampaign || ''}
              onChange={(e) => setSelectedCampaign(e.target.value ? Number(e.target.value) : null)}
              disabled={!selectedAccount}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="">All Campaigns</option>
              {campaigns.map((campaign) => (
                <option key={campaign.id} value={campaign.id}>
                  {campaign.display_name}
                </option>
              ))}
            </select>
          </div>

          {/* Start Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={filters.start_date}
              onChange={(e) => handleFilterChange('start_date', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* End Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={filters.end_date}
              onChange={(e) => handleFilterChange('end_date', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Bot/Human Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Traffic Type
            </label>
            <select
              value={filters.answer === undefined || filters.answer === null ? '' : filters.answer}
              onChange={(e) => handleFilterChange('answer', e.target.value === '' ? null : Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Traffic</option>
              <option value="1">Human Only</option>
              <option value="0">Bot Only</option>
            </select>
          </div>

          {/* Language Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Language
            </label>
            <input
              type="text"
              placeholder="e.g., en, ru"
              value={filters.language || ''}
              onChange={(e) => handleFilterChange('language', e.target.value || undefined)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-4 flex gap-3">
          <button
            onClick={() => refetch()}
            disabled={isLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
          >
            {isLoading ? 'Loading...' : 'Apply Filters'}
          </button>
          <button
            onClick={handleExport}
            disabled={isExporting || !logsData?.data?.length}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400"
          >
            {isExporting ? 'Exporting...' : 'Export to CSV'}
          </button>
        </div>
      </div>

      {/* Campaign Settings Panel */}
      {selectedCampaign && campaignDetail && (
        <CampaignSettingsPanel campaign={campaignDetail} />
      )}

      {/* Results Section */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {/* Results Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Logs</h2>
            {logsData && (
              <p className="text-sm text-gray-500 mt-1">
                Showing {logsData.from} to {logsData.to} of {logsData.total} results
              </p>
            )}
          </div>
          {logsData && logsData.last_page > 1 && (
            <div className="flex gap-2">
              <button
                onClick={() => handleFilterChange('page', Math.max(1, filters.page! - 1))}
                disabled={filters.page === 1}
                className="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="px-3 py-1 text-sm text-gray-700">
                Page {filters.page} of {logsData.last_page}
              </span>
              <button
                onClick={() => handleFilterChange('page', Math.min(logsData.last_page, filters.page! + 1))}
                disabled={filters.page === logsData.last_page}
                className="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </div>

        {/* Logs Table */}
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="p-8 text-center text-gray-500">Loading logs...</div>
          ) : !logsData?.data?.length ? (
            <div className="p-8 text-center text-gray-500">No logs found for the selected filters</div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Time
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    IP
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Country
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Language
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Browser
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Platform
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Decision
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Domain
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {logsData.data.map((log) => (
                  <>
                    <tr
                      key={log.request_id}
                      className={`hover:bg-gray-50 cursor-pointer ${
                        expandedRow === log.request_id ? 'bg-blue-50' : ''
                      }`}
                      onClick={() => toggleRowExpansion(log.request_id)}
                    >
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                        {format(new Date(log.timestamp), 'yyyy-MM-dd HH:mm:ss')}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                        {log.ip_address}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                        {log.country}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                        {log.language}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                        {log.browser} {log.browser_version}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                        {log.platform}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${
                            log.decision === 'Human'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {log.decision}
                        </span>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                        {log.domain}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRowExpansion(log.request_id);
                          }}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          {expandedRow === log.request_id ? 'Hide' : 'Details'}
                        </button>
                      </td>
                    </tr>

                    {/* Expanded Row */}
                    {expandedRow === log.request_id && (
                      <tr>
                        <td colSpan={9} className="px-3 py-4 bg-gray-50">
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 text-sm">
                            <DetailField label="Request ID" value={log.request_id} />
                            <DetailField label="Campaign ID" value={log.campaign_id} />
                            <DetailField label="Campaign Name" value={log.campaign_name} />
                            <DetailField label="Account Owner" value={`${log.account_owner_name} (${log.account_owner_email})`} />
                            <DetailField label="ASN" value={log.asn} />
                            <DetailField label="ISP" value={log.isp} />
                            <DetailField label="Usage Type" value={log.usage_type} />
                            <DetailField label="Proxy Type" value={log.proxy_type} />
                            <DetailField label="Device Type" value={log.device_type} />
                            <DetailField label="Block Reason" value={log.block_reason} />
                            <DetailField label="Filter Score" value={log.filter_score} />
                            <DetailField label="Referrer" value={log.referer} className="col-span-2" />
                            <DetailField label="User Agent" value={log.user_agent} className="col-span-2" />
                            <DetailField label="HTTP Accept" value={log.http_accept} className="col-span-2" />
                            <DetailField label="Accept Encoding" value={log.http_accept_encoding} />
                            <DetailField label="HTTP Priority" value={log.http_priority} />
                            <DetailField label="Fetch Mode" value={log.sec_fetch_mode} />
                            <DetailField label="Integration" value={`${log.integration_type} v${log.integration_version}`} />
                            <DetailField label="Target URL" value={log.target_url} className="col-span-full" />
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination Footer */}
        {logsData && logsData.last_page > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
            <div className="text-sm text-gray-700">
              Page {filters.page} of {logsData.last_page}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handleFilterChange('page', Math.max(1, filters.page! - 1))}
                disabled={filters.page === 1}
                className="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => handleFilterChange('page', Math.min(logsData.last_page, filters.page! + 1))}
                disabled={filters.page === logsData.last_page}
                className="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Helper component for detail fields
function DetailField({ label, value, className = '' }: { label: string; value: any; className?: string }) {
  return (
    <div className={`${className}`}>
      <dt className="text-xs font-medium text-gray-500">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900 break-words">{value || '-'}</dd>
    </div>
  );
}

// Tag list renderer
function TagList({ items }: { items: string[] | null | undefined }) {
  if (!items?.length) return <span className="text-gray-400 text-xs">None</span>;
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {items.map((item) => (
        <span key={item} className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-mono">
          {item}
        </span>
      ))}
    </div>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 pb-1 border-b border-gray-100">
      {children}
    </h3>
  );
}

const LOAD_MODE_LABELS: Record<string, string> = {
  redirect: 'Redirect',
  iframe: 'iFrame',
  require: 'Require (PHP)',
};

function loadModeLabel(mode: string | null | undefined): string {
  if (!mode) return '—';
  return LOAD_MODE_LABELS[mode] || mode;
}

function CampaignSettingsPanel({ campaign }: { campaign: CampaignDetail }) {
  const [showLegacy, setShowLegacy] = useState(false);
  const hasFilter = !!campaign.filter;
  const hasAbTest = !!(campaign.split_links?.length);

  return (
    <div className="bg-white shadow rounded-lg p-6 mb-6 space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 flex-wrap">
          <span className="w-1 h-5 bg-blue-500 rounded-full inline-block" />
          Campaign Settings — <span className="font-mono text-blue-700">{campaign.name}</span>
          <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-semibold ${
            campaign.status === 'active' ? 'bg-green-100 text-green-700' :
            campaign.status === 'paused' ? 'bg-yellow-100 text-yellow-700' :
            'bg-gray-100 text-gray-600'
          }`}>{campaign.status}</span>
          {campaign.tags?.length ? (
            <span className="flex flex-wrap gap-1">
              {campaign.tags.map((tag) => (
                <span
                  key={tag.id}
                  className="px-2 py-0.5 rounded-full text-xs font-medium"
                  style={{
                    backgroundColor: tag.color ? `${tag.color}20` : '#f3f4f6',
                    color: tag.color || '#4b5563',
                  }}
                >
                  {tag.name}
                </span>
              ))}
            </span>
          ) : null}
        </h2>

        {/* Meta info */}
        <div className="text-xs text-gray-500 space-y-0.5 text-right">
          <p>UUID: <span className="font-mono text-gray-700">{campaign.uuid}</span></p>
          <p>Created: <span className="text-gray-700">{format(new Date(campaign.created_at), 'yyyy-MM-dd HH:mm')}</span></p>
          <p>Manager: <span className="text-gray-700">{campaign.assigned_manager?.name || 'Unassigned'}</span></p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">

        {/* Pages */}
        <div>
          <SectionHeading>Pages</SectionHeading>
          <div className="space-y-3 text-sm">
            <div>
              <p className="text-xs font-medium text-gray-500">Human URL</p>
              <p className="mt-0.5 text-gray-900 break-all font-mono text-xs bg-green-50 px-2 py-1 rounded">
                {campaign.human_redirect_url || '—'}
              </p>
              <p className="mt-0.5 text-xs text-gray-400">
                {campaign.human_redirect_type} · Load Mode: {loadModeLabel(campaign.human_redirect_mode)}
              </p>
              {campaign.human_redirect_mode === 'iframe' && campaign.human_back_redirect_url ? (
                <p className="mt-0.5 text-xs text-gray-400 break-all">
                  Back button → <span className="font-mono">{campaign.human_back_redirect_url}</span>
                </p>
              ) : null}
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500">Bot URL</p>
              <p className="mt-0.5 text-gray-900 break-all font-mono text-xs bg-red-50 px-2 py-1 rounded">
                {campaign.bot_redirect_url || '—'}
              </p>
              <p className="mt-0.5 text-xs text-gray-400">{campaign.bot_redirect_type}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500">Domains</p>
              <TagList items={campaign.domains} />
            </div>
          </div>
        </div>

        {/* Tracking */}
        <div>
          <SectionHeading>Tracking</SectionHeading>
          <div className="space-y-3 text-sm">
            <div>
              <p className="text-xs font-medium text-gray-500">API Key</p>
              <p className="mt-0.5 text-gray-900 break-all font-mono text-xs bg-gray-50 px-2 py-1 rounded">
                {campaign.api_key}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500">Postback Param</p>
              <p className="mt-0.5 text-gray-900 font-mono text-xs">{campaign.postback_param || '—'}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500">Pass Query Params</p>
              <p className="mt-0.5 text-gray-900 text-xs">{campaign.pass_query_params ? 'Yes' : 'No'}</p>
            </div>
            {campaign.query_string ? (
              <div>
                <p className="text-xs font-medium text-gray-500">Custom Query String</p>
                <p className="mt-0.5 text-gray-900 font-mono text-xs break-all">{campaign.query_string}</p>
              </div>
            ) : null}
          </div>
        </div>

        {/* Filters */}
        <div>
          <SectionHeading>Filters</SectionHeading>
          {!hasFilter ? (
            <p className="text-sm text-gray-400">No filters configured</p>
          ) : (
            <div className="space-y-2 text-sm">
              {[
                ['Allowed Countries', campaign.filter?.allowed_countries],
                ['Blocked Countries', campaign.filter?.blocked_countries],
                ['Allowed Languages', campaign.filter?.allowed_languages],
                ['Blocked Languages', campaign.filter?.blocked_languages],
                ['Allowed OS', campaign.filter?.allowed_os],
                ['Blocked OS', campaign.filter?.blocked_os],
                ['Allowed Devices', campaign.filter?.allowed_devices],
                ['Blocked Devices', campaign.filter?.blocked_devices],
                ['Allowed Browsers', campaign.filter?.allowed_browsers],
                ['Blocked Browsers', campaign.filter?.blocked_browsers],
                ['Blocked Usage Types', campaign.filter?.blocked_usage_types],
                ['Blocked ISPs', campaign.filter?.blocked_isps],
              ].map(([label, values]) => (
                (values as string[] | null)?.length ? (
                  <div key={label as string}>
                    <p className="text-xs font-medium text-gray-500">{label as string}</p>
                    <TagList items={values as string[]} />
                  </div>
                ) : null
              ))}
            </div>
          )}
        </div>

        {/* A/B Test */}
        <div>
          <SectionHeading>A/B Test</SectionHeading>
          {!hasAbTest ? (
            <p className="text-sm text-gray-400">No A/B test configured</p>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-gray-500">
                Mode: <span className="font-semibold text-gray-700">{campaign.target_mode}</span>
              </p>
              {campaign.split_links!.map((variant, i) => (
                <div key={variant.id} className="bg-gray-50 rounded-lg p-3 text-sm">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-gray-800">
                      {i + 1}. {variant.name}
                    </span>
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-mono">
                      {variant.weight}%
                    </span>
                  </div>
                  <p className="text-xs font-mono text-gray-600 break-all">{variant.url}</p>
                  {variant.conditions?.rules?.length ? (
                    <div className="mt-2 text-xs text-gray-500">
                      <span className="font-medium">Conditions ({variant.conditions.match}):</span>
                      {variant.conditions.rules.map((rule, j) => (
                        <div key={j} className="ml-2 font-mono">
                          {rule.param} {rule.operator} [{rule.values.join(', ')}]
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>

      </div>

      {/* Legacy / Unused Fields */}
      <div className="border-t border-gray-100 pt-4">
        <button
          onClick={() => setShowLegacy(!showLegacy)}
          className="text-xs font-medium text-gray-400 hover:text-gray-600 flex items-center gap-1"
        >
          {showLegacy ? '▾' : '▸'} Legacy / unused fields (not read by the live cloaking engine)
        </button>
        {showLegacy && (
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm bg-gray-50 rounded-lg p-3">
            <DetailField label="content_mode" value={campaign.legacy.content_mode} />
            <DetailField label="target_link" value={campaign.legacy.target_link} className="col-span-2" />
            <DetailField label="partial_integration" value={campaign.legacy.partial_integration ? 'true' : 'false'} />
            <DetailField label="disable_blacklist" value={campaign.legacy.disable_blacklist ? 'true' : 'false'} />
            <DetailField
              label="config (JSON)"
              value={campaign.legacy.config ? JSON.stringify(campaign.legacy.config) : null}
              className="col-span-full"
            />
          </div>
        )}
      </div>
    </div>
  );
}
